import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { PlatformBadge } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Plus, Sparkle, ChartLine, ArrowsClockwise, Trash, Hash, Crosshair, Rocket, FileCsv, FileText, X, DotsSixVertical, Link as LinkIcon, Copy, Trophy, Eye as EyeIcon, Heart, ChatCircle, ShareFat, ArrowsDownUp, TrendUp, MagnifyingGlass, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSelection } from "@/hooks/useSelection";
import { useConfirm } from "@/hooks/useConfirm";
import BulkActionBar, { SelectAllCheckbox, RowCheckbox, runBulkDelete, BulkBarButton } from "@/components/BulkActionBar";
import { exportRowsToCsv, todayStamp } from "@/lib/csvExport";
import { LaunchPlanModal } from "@/components/posts/LaunchPlanModal";
import { KPIBox, PostRow, PostRowMobile } from "@/components/posts/PostRow";
import { PredictionBox } from "@/components/posts/PredictionBox";
import { PLATFORMS, SORT_OPTIONS, TONES, totalEngagement } from "@/components/posts/utils";

export default function Posts() {
  const { sucursalId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [openGen, setOpenGen] = useState(false);
  const [openPlan, setOpenPlan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [genForm, setGenForm] = useState({ product_id: "", plataforma: "instagram", tono: "amigable" });
  const [sortBy, setSortBy] = useState("score_desc");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const params = sucursalId ? { sucursal_id: sucursalId } : {};
    const [p, ps] = await Promise.all([
      api.get("/products", { params }),
      api.get("/posts", { params }),
    ]);
    setProducts(p.data);
    setPosts(ps.data);
  }, [sucursalId]);

  useEffect(() => {
    let cancelled = false;
    const params = sucursalId ? { sucursal_id: sucursalId } : {};
    (async () => {
      try {
        const [p, ps] = await Promise.all([
          api.get("/products", { params }),
          api.get("/posts", { params }),
        ]);
        if (cancelled) return;
        setProducts(p.data);
        setPosts(ps.data);
      } catch {
        // ignored — non-critical for initial render
      }
    })();
    return () => { cancelled = true; };
  }, [sucursalId]);

  // Auto-open the generator dialog when arriving from Inventory with a preselected product.
  // The state updates are deferred via queueMicrotask so the React 19
  // set-state-in-effect lint rule (which flags sync setState in effects)
  // sees them as scheduled work, not synchronous in-effect mutations.
  useEffect(() => {
    const productId = location.state?.productId;
    if (!productId || !products.some((p) => p.id === productId)) return;
    queueMicrotask(() => {
      setDraft(null);
      setGenForm({ product_id: productId, plataforma: "instagram", tono: "amigable" });
      setOpenGen(true);
      // Clear the state so reopening the page doesn't keep auto-triggering.
      navigate(location.pathname, { replace: true });
    });
  }, [location.state, location.pathname, products, navigate]);

  // Filter post list when arriving with viewProductId (from Inventory "see posts" badge)
  const viewProductId = location.state?.viewProductId || null;
  const baseFilteredPosts = viewProductId
    ? posts.filter((p) => p.product_id === viewProductId)
    : posts;
  const viewProduct = viewProductId ? products.find((p) => p.id === viewProductId) : null;

  // Sort copy of the filtered list according to the chosen sort
  const sortedPosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? baseFilteredPosts.filter((p) => {
          const prod = products.find((x) => x.id === p.product_id);
          const hay = `${p.titulo || ""} ${p.plataforma || ""} ${p.tono || ""} ${p.contenido || ""} ${(p.hashtags || []).join(" ")} ${prod?.nombre || ""}`.toLowerCase();
          return hay.includes(q);
        })
      : [...baseFilteredPosts];
    switch (sortBy) {
      case "score_desc":
        list.sort((a, b) => (b.score || 0) - (a.score || 0));
        break;
      case "score_asc":
        list.sort((a, b) => (a.score || 0) - (b.score || 0));
        break;
      case "vistas_desc":
        list.sort((a, b) => (b.metricas?.vistas || 0) - (a.metricas?.vistas || 0));
        break;
      case "engagement_desc":
        list.sort((a, b) => totalEngagement(b) - totalEngagement(a));
        break;
      case "recent":
      default:
        list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return list;
  }, [baseFilteredPosts, sortBy, query, products]);

  // Always pre-compute the score ranking — used for medal badges, regardless of current sort
  const scoreRanking = useMemo(() => {
    const ranked = [...baseFilteredPosts]
      .filter((p) => (p.score || 0) > 0)
      .sort((a, b) => (b.score || 0) - (a.score || 0));
    const map = {};
    ranked.forEach((p, idx) => {
      map[p.id] = idx + 1;
    });
    return map;
  }, [baseFilteredPosts]);

  // Aggregate KPIs at the top of the page
  const kpis = useMemo(() => {
    const arr = baseFilteredPosts;
    if (arr.length === 0) {
      return { count: 0, avgScore: 0, totalVistas: 0, totalEng: 0, top: null };
    }
    const totalVistas = arr.reduce((s, p) => s + (p.metricas?.vistas || 0), 0);
    const totalEng = arr.reduce((s, p) => s + totalEngagement(p), 0);
    const sumScore = arr.reduce((s, p) => s + (p.score || 0), 0);
    const top = [...arr].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
    return {
      count: arr.length,
      avgScore: Math.round(sumScore / arr.length),
      totalVistas,
      totalEng,
      top: top && top.score > 0 ? top : null,
    };
  }, [baseFilteredPosts]);

  // Backwards-compat alias used elsewhere in the JSX
  const filteredPosts = sortedPosts;

  const clearViewFilter = () => {
    navigate(location.pathname, { replace: true, state: {} });
  };

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const selection = useSelection(filteredPosts);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const onBulkDelete = async () => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const ok = await confirm({
      title: `Eliminar ${ids.length} publicación${ids.length === 1 ? "" : "es"}`,
      description: "Las métricas históricas (vistas, clicks, atribución de ventas) se conservarán en auditoría, pero los borradores no podrán recuperarse.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkDelete(ids, (id) => api.delete(`/posts/${id}`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} publicación(es) eliminada(s)`);
    if (fail) toast.error(`${fail} no se pudieron eliminar`);
    load();
  };

  const onBulkExport = () => {
    const ids = new Set(selection.selectedIds);
    const rows = filteredPosts.filter((p) => ids.has(p.id));
    if (!rows.length) return;
    exportRowsToCsv({
      filename: `publicaciones-${todayStamp()}.csv`,
      headers: [
        { key: "titulo", label: "Título" },
        { key: "plataforma", label: "Plataforma" },
        { key: "tono", label: "Tono" },
        { label: "Producto", value: (p) => productMap[p.product_id]?.nombre || "" },
        { label: "Score", value: (p) => p.score || 0 },
        { label: "Vistas", value: (p) => p.metricas?.vistas || 0 },
        { label: "Likes", value: (p) => p.metricas?.likes || 0 },
        { label: "Comentarios", value: (p) => p.metricas?.comentarios || 0 },
        { label: "Compartidos", value: (p) => p.metricas?.compartidos || 0 },
        { label: "Clicks", value: (p) => p.metricas?.clicks || 0 },
        { key: "version", label: "Versión" },
        { key: "contenido", label: "Contenido" },
        { label: "Hashtags", value: (p) => (p.hashtags || []).join(" ") },
      ],
      rows,
    });
    toast.success(`${rows.length} publicación(es) exportada(s) a CSV`);
  };

  const onGenerate = async () => {
    if (!genForm.product_id) return toast.error("Selecciona un producto");
    setLoading(true);
    try {
      const { data } = await api.post("/posts/generate", genForm);
      setDraft({ ...data, ...genForm, mejor_imagen_idx: data.mejor_imagen_idx || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    } finally {
      setLoading(false);
    }
  };

  const onSaveDraft = async () => {
    if (!draft) return;
    try {
      const prod = productMap[draft.product_id];
      const enlace = typeof window !== "undefined" ? `${window.location.origin}/p/${prod?.slug || draft.product_id}` : "";
      await api.post("/posts", {
        product_id: draft.product_id,
        plataforma: draft.plataforma,
        tono: draft.tono,
        titulo: draft.titulo,
        contenido: draft.contenido,
        hashtags: draft.hashtags || [],
        cta: draft.cta || "",
        imagenes_base64: draft.imagenes_base64 || [],
        mejor_imagen_idx: draft.mejor_imagen_idx || 0,
        prediccion: draft.prediccion || null,
        enlace_producto: enlace,
      });
      toast.success("Publicación guardada");
      setOpenGen(false);
      setDraft(null);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="posts-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
        <div>
          <div className="overline text-klein">Publicaciones</div>
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
            Genera, mide,
            <br />
            <span className="text-klein">mejora automáticamente.</span>
          </h1>
          <p className="text-muted2 mt-3 max-w-xl">
            Copy listo para redes. Si el desempeño no convence, IA reescribe con un nuevo ángulo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-ghost"
            onClick={() => setOpenPlan(true)}
            disabled={posts.length === 0}
            data-testid="btn-launch-plan"
            data-help="Programa el orden y horario de tus publicaciones de la semana arrastrándolas en un calendario.">
            <Rocket size={18} /> Plan de lanzamiento
          </button>
          <button
            className="btn-klein"
            onClick={() => {
              setDraft(null);
              setGenForm({
                product_id: products[0]?.id || "",
                plataforma: "instagram",
                tono: "amigable",
              });
              setOpenGen(true);
            }}
            disabled={products.length === 0}
            data-testid="btn-new-post"
            data-help="Crea una publicación con copy, hashtags y predicción de desempeño usando GPT-5.2."
          >
            <Plus size={18} weight="bold" /> Nueva publicación
          </button>
        </div>
      </div>

      {products.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-muted2">
          Necesitas al menos un producto en inventario para generar publicaciones.
        </div>
      )}

      {viewProduct && (
        <div
          className="mb-5 border border-klein/40 bg-klein/[0.05] p-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="posts-view-filter"
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="overline text-klein">Filtrado por producto</span>
            <span className="font-medium">{viewProduct.nombre}</span>
            <span className="text-muted2 text-xs">
              · {filteredPosts.length} publicación{filteredPosts.length === 1 ? "" : "es"}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setGenForm({ product_id: viewProduct.id, plataforma: "instagram", tono: "amigable" });
                setOpenGen(true);
              }}
              className="btn-klein text-xs px-3 py-1.5"
              data-testid="posts-view-create-new"
            >
              <Plus size={14} weight="bold" /> Crear nueva
            </button>
            <button
              type="button"
              onClick={clearViewFilter}
              className="btn-ghost text-xs px-3 py-1.5"
              data-testid="posts-view-clear"
            >
              <X size={14} /> Quitar filtro
            </button>
          </div>
        </div>
      )}

      {viewProduct && filteredPosts.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-muted2 mb-6">
          Este producto aún no tiene publicaciones. Pulsa <strong>Crear nueva</strong> arriba.
        </div>
      )}

      {baseFilteredPosts.length > 0 && (
        <>
          {/* Performance leaderboard */}
          <section className="mb-5 grid grid-cols-1 lg:grid-cols-5 gap-3" data-testid="posts-leaderboard">
            <div
              className={`border p-4 lg:col-span-2 ${
                kpis.top ? "bg-gradient-to-br from-klein to-klein-dark text-white border-klein" : "border-zinc-200"
              }`}
              data-testid="posts-top-performer"
            >
              <div className={`overline flex items-center gap-1.5 ${kpis.top ? "text-white/80" : "text-klein"}`}>
                <Trophy size={12} weight="fill" /> Post estrella
              </div>
              {kpis.top ? (
                <>
                  <div className="mt-2 text-lg font-bold leading-tight truncate">
                    {kpis.top.titulo}
                  </div>
                  <div className="text-xs text-white/70 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="uppercase tracking-wider">{kpis.top.plataforma}</span>
                    <span>·</span>
                    <span>{productMap[kpis.top.product_id]?.nombre || "—"}</span>
                  </div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <div className="h-display text-5xl font-black tabular-nums">{kpis.top.score || 0}</div>
                    <div className="text-xs text-white/70">score / 100</div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted2 mt-2">
                  Aún no tienes métricas registradas. Registra vistas/likes en cada post para ver aquí tu top performer.
                </div>
              )}
            </div>

            <KPIBox label="Publicaciones" value={kpis.count} icon={ChartLine} />
            <KPIBox label="Score promedio" value={kpis.avgScore} icon={TrendUp} suffix={<span className="text-xs">/100</span>} />
            <KPIBox label="Interacción total" value={kpis.totalEng} icon={Heart} />
          </section>

          {/* Sort + visible count */}
          <div
            className="mb-5 flex items-center justify-between gap-3 flex-wrap"
            data-testid="posts-sort-bar"
          >
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" />
              <input
                type="text"
                className="input-flat pl-8"
                placeholder="Buscar título, plataforma, hashtag…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="posts-search"
                data-help="Filtra las publicaciones por título, plataforma, tono, hashtag o nombre del producto."
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted2 hover:text-ink"
                  data-testid="posts-search-clear"
                  title="Limpiar"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="overline flex items-center gap-1 text-muted2">
                <ArrowsDownUp size={12} /> Ordenar
              </label>
              <select
                className="border border-zinc-300 px-2 py-1.5 text-xs bg-white"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="posts-sort-select"
                data-help="Cambia el orden para ver rápido cuál está funcionando mejor o cuál necesita mejorar."
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      <BulkActionBar
        count={selection.count}
        noun="publicación"
        pluralNoun="publicaciones"
        onDelete={onBulkDelete}
        onClear={selection.clear}
        busy={bulkBusy}
        testid="posts-bulk-bar"
        extraActions={
          <BulkBarButton
            onClick={onBulkExport}
            icon={Download}
            label="Exportar CSV"
            testid="posts-bulk-bar-export"
            disabled={bulkBusy}
          />
        }
      />
      {confirmDialog}

      <div className="border border-zinc-200 hidden lg:block" data-testid="posts-table">
        {filteredPosts.length > 0 && (
          <div className="grid grid-cols-12 px-4 py-3 bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-[0.2em] font-medium text-muted2 items-center">
            <div className="col-span-1 flex items-center">
              <SelectAllCheckbox
                allSelected={selection.allSelected}
                someSelected={selection.someSelected}
                onChange={selection.toggleAll}
                testid="posts-select-all"
              />
            </div>
            <div className="col-span-4">Publicación</div>
            <div className="col-span-2">Plataforma</div>
            <div className="col-span-2">Producto</div>
            <div className="col-span-1 text-right">Score</div>
            <div className="col-span-1 text-right">Interacción</div>
            <div className="col-span-1 text-right">Acciones</div>
          </div>
        )}
        {filteredPosts.length === 0 && products.length > 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted2">
            Aún no hay publicaciones. Pulsa <strong>Nueva publicación</strong> arriba.
          </div>
        )}
        {filteredPosts.map((p) => (
          <PostRow
            key={p.id}
            post={p}
            product={productMap[p.product_id]}
            rank={scoreRanking[p.id] || null}
            onChanged={load}
            selected={selection.isSelected(p.id)}
            onToggleSelect={() => selection.toggle(p.id)}
          />
        ))}
      </div>

      {/* Mobile / tablet cards */}
      <div className="lg:hidden space-y-3" data-testid="posts-mobile-cards">
        {filteredPosts.length === 0 && products.length > 0 && (
          <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-muted2">
            Aún no hay publicaciones.
          </div>
        )}
        {filteredPosts.map((p) => (
          <PostRowMobile
            key={p.id}
            post={p}
            product={productMap[p.product_id]}
            rank={scoreRanking[p.id] || null}
            onChanged={load}
            selected={selection.isSelected(p.id)}
            onToggleSelect={() => selection.toggle(p.id)}
          />
        ))}
      </div>

      {openPlan && (
        <LaunchPlanModal
          posts={posts}
          products={productMap}
          onClose={() => setOpenPlan(false)}
        />
      )}

      {/* Generate dialog */}
      {openGen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setOpenGen(false)}
        >
          <div
            className="bg-white border border-zinc-200 max-w-2xl w-full p-8 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="generate-dialog"
          >
            <div className="overline text-klein">Generador IA</div>
            <h2 className="h-display text-3xl font-bold tracking-tight mt-1 mb-6">
              Nueva publicación
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="overline block mb-1.5">Producto</label>
                <select
                  className="input-flat"
                  value={genForm.product_id}
                  onChange={(e) => setGenForm({ ...genForm, product_id: e.target.value })}
                  data-testid="gen-product"
                  data-help="Producto sobre el que se generará el post. Si está vacío, primero crea uno en Inventario o Escanear con AI."
                >
                  <option value="">— Selecciona —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} ({p.categoria})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline block mb-1.5">Plataforma</label>
                <select
                  className="input-flat"
                  value={genForm.plataforma}
                  onChange={(e) => setGenForm({ ...genForm, plataforma: e.target.value })}
                  data-testid="gen-platform"
                  data-help="Red social destino. La IA adapta longitud, hashtags y tono al formato de cada plataforma."
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-1.5">Tono</label>
                <select
                  className="input-flat"
                  value={genForm.tono}
                  onChange={(e) => setGenForm({ ...genForm, tono: e.target.value })}
                  data-testid="gen-tone"
                  data-help="Estilo de escritura: amigable, profesional, urgente, etc. Define la voz de la marca."
                >
                  {TONES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="btn-klein mt-4"
              disabled={loading}
              onClick={onGenerate}
              data-testid="btn-generate"
              data-help="GPT-5.2 escribe título, copy, hashtags y predice un score de desempeño esperado."
            >
              <Sparkle size={18} weight="fill" /> {loading ? "Generando…" : "Generar copy"}
            </button>

            {draft && (
              <div className="mt-6 border border-zinc-200 p-5 animate-fade-in">
                <div className="overline mb-2">Vista previa</div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  {draft.imagenes_base64?.length > 0 && (
                    <div className="md:col-span-5">
                      <div className="aspect-square w-full bg-zinc-50 border border-zinc-200 overflow-hidden">
                        <img
                          src={`data:image/jpeg;base64,${draft.imagenes_base64[draft.mejor_imagen_idx || 0]}`}
                          alt="portada"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {draft.imagenes_base64.length > 1 && (
                        <>
                          <div className="grid grid-cols-6 gap-1 mt-2">
                            {draft.imagenes_base64.map((b64, i) => (
                              <button
                                key={`${b64?.slice(-16) || "img"}-${i}`}
                                onClick={() => setDraft({ ...draft, mejor_imagen_idx: i })}
                                className={`relative aspect-square overflow-hidden border ${
                                  (draft.mejor_imagen_idx || 0) === i
                                    ? "border-klein ring-2 ring-klein"
                                    : "border-zinc-200"
                                }`}
                                data-testid={`draft-thumb-${i}`}
                                title={`Foto ${i + 1}`}
                              >
                                <img
                                  src={`data:image/jpeg;base64,${b64}`}
                                  alt={`thumb ${i + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                          {draft.razon_imagen && (
                            <div className="text-[11px] text-muted2 mt-2 leading-snug">
                              <span className="overline">IA elige</span> {draft.razon_imagen}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div className={draft.imagenes_base64?.length > 0 ? "md:col-span-7" : "md:col-span-12"}>
                    <div className="h-display text-2xl font-bold tracking-tight">{draft.titulo}</div>
                    <p className="text-sm mt-3 whitespace-pre-line leading-relaxed">{draft.contenido}</p>
                    {draft.cta && (
                      <div className="mt-3 text-sm">
                        <span className="overline">CTA</span>{" "}
                        <span className="font-medium">{draft.cta}</span>
                      </div>
                    )}
                    {draft.hashtags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {draft.hashtags.map((h) => (
                          <span key={h} className="text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-200">
                            #{h}
                          </span>
                        ))}
                      </div>
                    )}
                    {draft.prediccion && (draft.prediccion.score_max > 0 || draft.prediccion.score_min > 0) && (
                      <PredictionBox p={draft.prediccion} />
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                  <button className="btn-ghost" onClick={() => setDraft(null)} data-testid="btn-discard">
                    Descartar
                  </button>
                  <button className="btn-klein" onClick={onSaveDraft} data-testid="btn-save-post">
                    Guardar publicación
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button className="btn-ghost" onClick={() => setOpenGen(false)} data-testid="btn-close-gen">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
